import { InjectionToken } from '@angular/core';

import { BuildEnvironment } from '@shared/models';
import { Brand, getBrand } from '@shared/services/branding';

export interface AppConfig {
  name: BuildEnvironment;
  apiHost: string;
  companyDomain: string;
  supersetDomain: string;
  /**
   * External API documentation portal (auth footer "API Documentation" link).
   * Brand- and environment-specific, so it lives here rather than in the brand
   * config — each brand has its own host case below.
   */
  docsUrl: string;
  firebase: Record<string, string>;
  production: boolean;
}

// Root-provided with a default factory so the token resolves anywhere, including
// bare TestBeds. The app and the testing module still override it explicitly.
export const APP_ENV_CONFIG = new InjectionToken<AppConfig>('Application Environment Config', {
  providedIn: 'root',
  factory: () => getEnvironment(),
});

export const getEnvironment = (): AppConfig => {
  switch (window.location.host) {
    case 'portal.rocketkor.net':
      return {
        name: 'production',
        production: true,
        apiHost: 'https://api.rocketkor.net',
        companyDomain: 'https://portal.rocketkor.net',
        supersetDomain: 'https://insights-prod01.rocketkor.net',
        docsUrl: 'https://docs.rocketkor.net/',
        firebase: {
          apiKey: 'AIzaSyCsP8Y-00FmGPqMPd7oDozyN6pToQX4ekk',
          authDomain: 'prod-rocketkor.firebaseapp.com',
          projectId: 'prod-rocketkor',
          storageBucket: 'prod-rocketkor.appspot.com',
          messagingSenderId: '681232479177',
          appId: '1:681232479177:web:e8066e71b675edf1646e18',
          measurementId: 'G-2KXHTN4G99',
        },
      };
    case 'portal.salttreasury.com':
      return {
        name: 'production',
        production: true,
        apiHost: 'https://api.salttreasury.com',
        companyDomain: 'https://portal.salttreasury.com',
        supersetDomain: 'https://insights.salttreasury.com',
        docsUrl: 'https://docs.salttreasury.com/',
        firebase: {
          apiKey: 'AIzaSyCsP8Y-00FmGPqMPd7oDozyN6pToQX4ekk',
          authDomain: 'prod-rocketkor.firebaseapp.com',
          projectId: 'prod-rocketkor',
          storageBucket: 'prod-rocketkor.appspot.com',
          messagingSenderId: '681232479177',
          appId: '1:681232479177:web:e8066e71b675edf1646e18',
          measurementId: 'G-2KXHTN4G99',
        },
      };
    case 'portal.sandbox.rocketkor.net':
      return {
        name: 'sandbox',
        production: true,
        apiHost: 'https://api.sandbox.rocketkor.net',
        companyDomain: 'https://portal.sandbox.rocketkor.net',
        supersetDomain: 'https://insights-sandbox01.rocketkor.net',
        docsUrl: 'https://docs.rocketkor.net/',
        firebase: {
          apiKey: 'AIzaSyDRVI7IUPWfqplVP_8tp_Ub55GauQ41Jgs',
          authDomain: 'cs-rocketkor.firebaseapp.com',
          projectId: 'cs-rocketkor',
          storageBucket: 'cs-rocketkor.appspot.com',
          messagingSenderId: '860477795296',
          appId: '1:860477795296:web:314d1c2b297636158a1985',
          measurementId: 'G-D3LVGG0ME9',
        },
      };
    case 'portal.sandbox.salttreasury.com':
      return {
        name: 'sandbox',
        production: true,
        apiHost: 'https://api.sandbox.salttreasury.com',
        companyDomain: 'https://portal.sandbox.salttreasury.com',
        supersetDomain: 'https://insights.sandbox.salttreasury.com',
        docsUrl: 'https://docs.salttreasury.com/',
        firebase: {
          apiKey: 'AIzaSyDRVI7IUPWfqplVP_8tp_Ub55GauQ41Jgs',
          authDomain: 'cs-rocketkor.firebaseapp.com',
          projectId: 'cs-rocketkor',
          storageBucket: 'cs-rocketkor.appspot.com',
          messagingSenderId: '860477795296',
          appId: '1:860477795296:web:314d1c2b297636158a1985',
          measurementId: 'G-D3LVGG0ME9',
        },
      };
    case 'portal.staging.rocketkor-nonprod.net':
      return {
        name: 'staging',
        production: false,
        apiHost: 'https://api.staging.rocketkor-nonprod.net',
        companyDomain: 'https://portal.staging.rocketkor-nonprod.net',
        supersetDomain: 'https://insights-staging01.rocketkor-nonprod.net',
        docsUrl: 'https://docs.staging.rocketkor-nonprod.net/',
        firebase: {
          apiKey: 'AIzaSyCXIY38rL7UGVvi6XTdLS6IfiqKG_XFLds',
          authDomain: 'staging-rocketkor.firebaseapp.com',
          projectId: 'staging-rocketkor',
          storageBucket: 'staging-rocketkor.firebasestorage.app',
          messagingSenderId: '869929627660',
          appId: '1:869929627660:web:41ea133dbd66e1ee25be29',
          measurementId: 'G-EMJNPYD9QH',
        },
      };
    case 'portal.staging.salttreasury.com':
      return {
        name: 'staging',
        production: false,
        apiHost: 'https://api.staging.salttreasury.com',
        companyDomain: 'https://portal.staging.salttreasury.com',
        supersetDomain: 'https://insights.staging.salttreasury.com',
        docsUrl: 'https://docs.staging.salttreasury.com/',
        firebase: {
          apiKey: 'AIzaSyCXIY38rL7UGVvi6XTdLS6IfiqKG_XFLds',
          authDomain: 'staging-rocketkor.firebaseapp.com',
          projectId: 'staging-rocketkor',
          storageBucket: 'staging-rocketkor.firebasestorage.app',
          messagingSenderId: '869929627660',
          appId: '1:869929627660:web:41ea133dbd66e1ee25be29',
          measurementId: 'G-EMJNPYD9QH',
        },
      };
    // SALT local dev runs against the QA backend (the deployed SALT env has its own case below).
    case 'salt.localhost':
    case 'salt.localhost:4200':
    case 'rocketkor-qa-portal.web.app':
    case 'rocketkor-qa-portal.firebaseapp.com':
    case 'portal.qa.rocketkor-nonprod.net':
      return {
        name: 'qa',
        production: false,
        apiHost: 'https://api.qa.rocketkor-nonprod.net',
        companyDomain: 'https://portal.qa.rocketkor-nonprod.net',
        supersetDomain: 'https://insights-qa01.rocketkor-nonprod.net',
        // SALT local dev shares this KOR-nonprod backend, so the docs link follows the brand.
        docsUrl: getBrand() === Brand.Salt ? 'https://docs.qa.salttreasury.com/' : 'https://docs.qa.rocketkor-nonprod.net/',
        firebase: {
          apiKey: 'AIzaSyCtIU_yvJRCCIYkCsJSbS4pNO8TamlP50U',
          authDomain: 'qa-rocketkor.firebaseapp.com',
          projectId: 'qa-rocketkor',
          storageBucket: 'qa-rocketkor.appspot.com',
          messagingSenderId: '941992412175',
          appId: '1:941992412175:web:80b541e4dfe1154d800f7a',
          measurementId: 'G-L5HN80PTH3',
        },
      };
    case 'portal.qa.salttreasury.com':
      return {
        name: 'qa',
        production: false,
        apiHost: 'https://api.qa.salttreasury.com',
        companyDomain: 'https://portal.qa.salttreasury.com',
        supersetDomain: 'https://insights.qa.salttreasury.com',
        docsUrl: 'https://docs.qa.salttreasury.com/',
        firebase: {
          apiKey: 'AIzaSyCtIU_yvJRCCIYkCsJSbS4pNO8TamlP50U',
          authDomain: 'qa-rocketkor.firebaseapp.com',
          projectId: 'qa-rocketkor',
          storageBucket: 'qa-rocketkor.appspot.com',
          messagingSenderId: '941992412175',
          appId: '1:941992412175:web:80b541e4dfe1154d800f7a',
          measurementId: 'G-L5HN80PTH3',
        },
      };
    case 'rocketkor-dev-portal.web.app':
    case 'rocketkor-dev-portal.firebaseapp.com':
    case 'portal.dev.rocketkor-nonprod.net':
      return {
        name: 'develop',
        production: false,
        apiHost: 'https://api.dev.rocketkor-nonprod.net',
        companyDomain: 'https://portal.dev.rocketkor-nonprod.net',
        supersetDomain: 'https://insights-dev01.rocketkor-nonprod.net',
        docsUrl: 'https://docs.dev.rocketkor-nonprod.net/',
        firebase: {
          apiKey: 'AIzaSyAgLrhRS6xRB6yKr_Qp5Kc3J1UBe7c-ffM',
          authDomain: 'dev-rocketkor.firebaseapp.com',
          projectId: 'dev-rocketkor',
          storageBucket: 'dev-rocketkor.appspot.com',
          messagingSenderId: '488449785816',
          appId: '1:488449785816:web:e462e770b90cba490ffcad',
        },
      };
    default:
      return {
        name: 'qa',
        production: false,
        apiHost: 'https://api.qa.rocketkor-nonprod.net',
        companyDomain: 'https://portal.qa.rocketkor-nonprod.net',
        supersetDomain: 'https://insights-qa01.rocketkor-nonprod.net',
        // SALT local dev shares this KOR-nonprod backend, so the docs link follows the brand.
        docsUrl: getBrand() === Brand.Salt ? 'https://docs.qa.salttreasury.com/' : 'https://docs.qa.rocketkor-nonprod.net/',
        firebase: {
          apiKey: 'AIzaSyCtIU_yvJRCCIYkCsJSbS4pNO8TamlP50U',
          authDomain: 'qa-rocketkor.firebaseapp.com',
          projectId: 'qa-rocketkor',
          storageBucket: 'qa-rocketkor.appspot.com',
          messagingSenderId: '941992412175',
          appId: '1:941992412175:web:80b541e4dfe1154d800f7a',
          measurementId: 'G-L5HN80PTH3',
        },
      };
  }
};
